/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import {
    ClusterPriceProto,
    ClusterPriceProtoPrice,
    ClusterProto,
    ClusterProtoLogBatch,
    ClusterProtoLogBatchLogLine,
    ClusterProtoServer,
    ClusterProtoServerLogBatch,
    ClusterProtoServerStatus,
    ClusterProtoSnapshot,
    ClusterProtoSnapshotConfiguration,
    ClusterProtoSnapshotConfigurationFrequency,
    ClusterProtoSnapshotType,
    ClusterProtoStatus
} from "../../protocol/concept";
import { bytesToString, stringToBytesOrUndefined, stripUndefinedValues, toSentenceCase } from "../util";
import { BaseResource, PartialWithUuid, Property } from "./base";
import {
    MachineType,
    machineTypeOf,
    Provider,
    providerOf,
    providerProtoOf,
    Region,
    regionOf,
    regionProtoOf,
    StorageType,
    storageTypeOf,
    storageTypeProtoOf,
    TypeDBVersion,
    typeDBVersionOf,
    typeDBVersionProtoOf
} from "./cluster-deployment-options";
import { partialSpaceOf, Space, spaceProtoOf } from "./space";
import { ClusterCtlProtoCheckClusterAutoPauseRes } from "../../application/protocol/cluster-controller";
import { APIToken, partialApiTokenOf } from "./api-token";
import { partialUserOf, User } from "./user";

export interface Cluster extends BaseResource {
    serverCount: number;
    provider: Provider;
    region: Region;
    machineType: MachineType;
    storageType: StorageType;
    storageGB: number;
    typeDBVersion: TypeDBVersion;
    status: ClusterStatus;
    createdAt: Date;
    space: Space;
    sampleDataset: string;
    isAdminUserInitialized: boolean;
    shortId: string;
    snapshotConfiguration: Partial<SnapshotConfiguration>;
    isFree: boolean;
    createdByUser?: Partial<User>;
    createdByAPIToken?: Partial<APIToken>;
}

export function partialClusterOf(data: ClusterProto): PartialWithUuid<Cluster> {
    const cluster: PartialWithUuid<Cluster> = {
        uuid: bytesToString(data.uuid),
        id: data.hasId ? data.id : undefined,
        serverCount: data.hasServerCount ? data.serverCount : undefined,
        provider: data.hasProvider ? providerOf(data.provider) : undefined,
        region: data.hasRegion ? regionOf(data.region) : undefined,
        machineType: data.hasMachineType ? machineTypeOf(data.machineType) : undefined,
        storageType: data.hasStorageType ? storageTypeOf(data.storageType) : undefined,
        storageGB: data.hasStorageGb ? data.storageGb : undefined,
        typeDBVersion: data.hasTypedbCloudVersion ? typeDBVersionOf(data.typedbCloudVersion) : undefined,
        status: data.hasStatus ? clusterStatusOf(data.status) : undefined,
        createdAt: data.hasCreatedAt ? new Date(data.createdAt) : undefined,
        space: data.hasSpace ? partialSpaceOf(data.space) as Space : undefined,
        sampleDataset: data.hasSampleDataset ? data.sampleDataset : undefined,
        isAdminUserInitialized: data.hasIsAdminUserInitialized ? data.isAdminUserInitialized : undefined,
        shortId: data.hasShortId ? data.shortId : undefined,
        snapshotConfiguration: data.hasSnapshotConfiguration ? snapshotConfigurationOf(data.snapshotConfiguration) : undefined,
        isFree: data.hasIsFree ? data.isFree : undefined,
        createdByUser: data.hasUser ? partialUserOf(data.user) : undefined,
        createdByAPIToken: data.hasApiToken ? partialApiTokenOf(data.apiToken) : undefined,
    };
    return stripUndefinedValues(cluster) as PartialWithUuid<Cluster>;
}

export function clusterProtoOf(data: Partial<Cluster>): ClusterProto {
    return new ClusterProto({
        uuid: stringToBytesOrUndefined(data.uuid),
        id: data.id,
        serverCount: data.serverCount,
        provider: data.provider ? providerProtoOf(data.provider) : undefined,
        region: data.region ? regionProtoOf(data.region) : undefined,
        machineType: data.machineType ? data.machineType.toProto() : undefined,
        storageType: data.storageType ? storageTypeProtoOf(data.storageType) : undefined,
        storageGb: data.storageGB,
        typedbCloudVersion: data.typeDBVersion ? typeDBVersionProtoOf(data.typeDBVersion) : undefined,
        status: data.status ? clusterStatusProtoOf(data.status) : undefined,
        createdAt: data.createdAt ? data.createdAt.getTime() : undefined,
        space: data.space ? spaceProtoOf(data.space) : undefined,
        sampleDataset: data.sampleDataset,
        isAdminUserInitialized: data.isAdminUserInitialized,
        shortId: data.shortId,
        snapshotConfiguration: data.snapshotConfiguration ? snapshotConfigurationProtoOf(data.snapshotConfiguration) : undefined,
        isFree: data.isFree
    });
}

export type ClusterStatus =
    "starting" | "running" | "pausing" | "paused" | "resuming" | "restoring" | "destroying" | "destroyed" | "updatingTypeDB" | "reconfiguring" | "migrating" | "error";

export const clusterStatusFromProtoMap: Record<ClusterProtoStatus, ClusterStatus> = {
    [ClusterProtoStatus.STARTING]: "starting",
    [ClusterProtoStatus.RUNNING]: "running",
    [ClusterProtoStatus.PAUSING]: "pausing",
    [ClusterProtoStatus.PAUSED]: "paused",
    [ClusterProtoStatus.RESUMING]: "resuming",
    [ClusterProtoStatus.RESTORING]: "restoring",
    [ClusterProtoStatus.DESTROYING]: "destroying",
    [ClusterProtoStatus.DESTROYED]: "destroyed",
    [ClusterProtoStatus.UPDATING_TYPEDB]: "updatingTypeDB",
    [ClusterProtoStatus.RECONFIGURING]: "reconfiguring",
    [ClusterProtoStatus.MIGRATING]: "migrating",
    [ClusterProtoStatus.ERROR]: "error"
};

const clusterStatusToProtoMap = Object.fromEntries(
    Object.entries(clusterStatusFromProtoMap).map(([proto, status]) => [status, proto])
) as unknown as Record<ClusterStatus, ClusterProtoStatus>;

function clusterStatusOf(status: ClusterProtoStatus): ClusterStatus {
    return clusterStatusFromProtoMap[status];
}

function clusterStatusProtoOf(status: ClusterStatus): ClusterProtoStatus {
    return clusterStatusToProtoMap[status];
}

export function clusterStatusToString(status: ClusterStatus) {
    if (status === "updatingTypeDB") return "Updating TypeDB";
    else return toSentenceCase(status);
}

const canBeResumedStatuses: ClusterStatus[] = ["pausing", "paused"];

export function isInResumableState(cluster: Cluster) {
    return canBeResumedStatuses.includes(cluster.status);
}

const canBePausedStatuses: ClusterStatus[] = ["starting", "running", "resuming", "updatingTypeDB", "reconfiguring", "error"];

export function isInPausableState(cluster: Cluster) {
    return canBePausedStatuses.includes(cluster.status);
}

const canBeDestroyedStatuses: ClusterStatus[] = ["starting", "running", "resuming", "pausing", "paused", "updatingTypeDB", "reconfiguring", "migrating", "error"];

export function isInDestroyableState(cluster: Cluster) {
    return canBeDestroyedStatuses.includes(cluster.status);
}

const canConnectStatuses: ClusterStatus[] = ["running", "resuming", "starting"];

export function isInConnectableState(cluster: Cluster) {
    return canConnectStatuses.includes(cluster.status);
}

export function storageSizeToString(storageGB: number): string {
    if (storageGB < 1000) return `${storageGB} GB`;
    else return `${storageGB / 1000} TB`;
}

export type ClusterColumn =
    | "id" | "space" | "provider" | "region" | "machineType" | "clusterSize" | "storageType" | "storageSize"
    | "clusterStatus" | "typeDBVersion" | "createdAt" | "backupFrequency";

export const clusterProperties: Record<ClusterColumn, Property> = {
    id: { id: "id", name: "Cluster Name", attributeType: "id" },
    space: { id: "space", name: "Space", ownerType: "space", attributeType: "id" },
    provider: { id: "provider", name: "Provider", ownerType: "provider", attributeType: "name" },
    region: { id: "region", name: "Region", ownerType: "region", attributeType: "vendor-id" },
    machineType: { id: "machineType", name: "Machine", ownerType: "machine-type", attributeType: "vendor-id" },
    clusterSize: { id: "clusterSize", name: "Servers", attributeType: "server-count" },
    storageType: { id: "storageType", name: "Storage Type", ownerType: "storage-type", attributeType: "vendor-id" },
    storageSize: { id: "storageSize", name: "Storage (GB)", attributeType: "storage-size-gb" },
    clusterStatus: { id: "clusterStatus", name: "Status", attributeType: "cluster-status" },
    typeDBVersion: { id: "typeDBVersion", name: "TypeDB Version", ownerType: "typedb-cloud", attributeType: "version" },
    createdAt: { id: "createdAt", name: "Creation Date", attributeType: "created-at" },
    backupFrequency: { id: "backupFrequency", name: "Backups", ownerType: "snapshot-configuration", attributeType: "frequency" }
};

export const clusterPropertiesList = [
    clusterProperties.id, clusterProperties.space, clusterProperties.provider, clusterProperties.region,
    clusterProperties.machineType, clusterProperties.clusterSize, clusterProperties.backupFrequency, clusterProperties.storageType,
    clusterProperties.storageSize, clusterProperties.clusterStatus, clusterProperties.typeDBVersion,
    clusterProperties.createdAt
];

export interface Server {
    uuid: string;
    id: number;
    address?: string;
    status: ServerStatus;
}

export function serverOf(server: ClusterProtoServer): Server {
    return {
        uuid: bytesToString(server.uuid),
        id: server.id,
        address: server.hasAddress ? server.address : undefined,
        status: server.hasStatus ? serverStatusOf(server.status) : "error",
    };
}

function serverProtoOf(server: Server): ClusterProtoServer {
    return new ClusterProtoServer({ address: server.address, status: serverStatusProtoOf(server.status) });
}

export type ServerStatus = "unknown" | "pending" | "running" | "error";

const serverStatusFromProtoMap: Record<ClusterProtoServerStatus, ServerStatus> = {
    [ClusterProtoServerStatus.UNKNOWN]: "unknown",
    [ClusterProtoServerStatus.PENDING]: "pending",
    [ClusterProtoServerStatus.RUNNING]: "running",
    [ClusterProtoServerStatus.ERROR]: "error",
};

const serverStatusToProtoMap = Object.fromEntries(
    Object.entries(serverStatusFromProtoMap).map(([proto, status]) => [status, proto])
) as unknown as Record<ServerStatus, ClusterProtoServerStatus>;

function serverStatusOf(status: ClusterProtoServerStatus): ServerStatus {
    return serverStatusFromProtoMap[status];
}

function serverStatusProtoOf(status: ServerStatus): ClusterProtoServerStatus {
    return serverStatusToProtoMap[status];
}

export function serverStatusToString(status: ServerStatus) {
    return toSentenceCase(status);
}

export interface ServerLogBatch {
    lines: string[];
    lineNumberStart: number;
    isEndOfFile: boolean;
}

export function serverLogBatchOf(proto: ClusterProtoServerLogBatch): ServerLogBatch {
    return {
        lines: proto.lines,
        lineNumberStart: proto.lineNumberStart,
        isEndOfFile: proto.isEndOfFile
    }
}

export interface ClusterLogBatch {
    lines: ClusterLogLine[];
    lineNumberStart: number;
    isEndOfFile: boolean;
}

export interface ClusterLogLine {
    line: string;
    timestamp: Date;
    serverIndex: number;
    lineNumber: number;
}

export function clusterLogBatchOf(proto: ClusterProtoLogBatch): ClusterLogBatch {
    return {
        lines: proto.lines.map((line, index) => clusterLogLineOf(line, proto.lineNumberStart + index)),
        lineNumberStart: proto.lineNumberStart,
        isEndOfFile: proto.isEndOfFile
    }
}

export function clusterLogLineOf(proto: ClusterProtoLogBatchLogLine, lineNumber: number): ClusterLogLine {
    return {
        line: proto.line,
        timestamp: new Date(proto.timestamp),
        serverIndex: proto.serverIndex,
        lineNumber
    }
}

type Price = { thousandthCentsPerHour: number, baseThousandthCentsPerHour?: number };

function priceOf(proto: ClusterPriceProtoPrice) {
    return {
        thousandthCentsPerHour: proto.thousandthCentsPerHour,
        baseThousandthCentsPerHour: proto.hasBaseThousandthCentsPerHour ? proto.baseThousandthCentsPerHour : undefined,
    };
}

export interface ClusterPrice {
    running: Price;
    paused: Price;
    snapshots?: Price;
}

export function clusterPriceOf(proto: ClusterPriceProto) {
    return { running: priceOf(proto.running), paused: priceOf(proto.paused), snapshots: proto.hasSnapshots ? priceOf(proto.snapshots) : undefined };
}

export interface SnapshotConfiguration {
    frequency: Frequency;
    retentionDays: number;
}

export type Frequency = "disabled" | "hourly" | "daily" | "weekly";

export function snapshotConfigurationOf(snapshotConfig: ClusterProtoSnapshotConfiguration): SnapshotConfiguration {
    return {
        frequency: frequencyOf(snapshotConfig.frequency),
        retentionDays: snapshotConfig.retentionDays,
    }
}

export function snapshotConfigurationProtoOf(snapshotConfig: Partial<SnapshotConfiguration>): ClusterProtoSnapshotConfiguration {
    return new ClusterProtoSnapshotConfiguration({
        frequency: snapshotConfig.frequency ? frequencyProtoOf(snapshotConfig.frequency) : undefined,
        retentionDays: snapshotConfig.retentionDays
    });
}

export function frequencyOf(frequency: ClusterProtoSnapshotConfigurationFrequency): Frequency {
    switch (frequency) {
        case ClusterProtoSnapshotConfigurationFrequency.DISABLED: return "disabled"
        case ClusterProtoSnapshotConfigurationFrequency.HOURLY: return "hourly"
        case ClusterProtoSnapshotConfigurationFrequency.DAILY: return "daily"
        case ClusterProtoSnapshotConfigurationFrequency.WEEKLY: return "weekly"
    }
}

export function frequencyProtoOf(frequency: Frequency): ClusterProtoSnapshotConfigurationFrequency {
    switch (frequency) {
        case "disabled": return ClusterProtoSnapshotConfigurationFrequency.DISABLED
        case "hourly": return ClusterProtoSnapshotConfigurationFrequency.HOURLY
        case "daily": return ClusterProtoSnapshotConfigurationFrequency.DAILY
        case "weekly": return ClusterProtoSnapshotConfigurationFrequency.WEEKLY
    }
}

export type SnapshotType = "Automatic" | "Manual";

export function snapshotTypeOf(type: ClusterProtoSnapshotType): SnapshotType {
    switch (type) {
        case ClusterProtoSnapshotType.AUTOMATIC: return "Automatic";
        case ClusterProtoSnapshotType.MANUAL: return "Manual";
    }
}

export interface Snapshot {
    id: string;
    createdAt: Date;
    restoreSize: number;
    type: SnapshotType;
}

export function snapshotOf(snapshot: ClusterProtoSnapshot): Snapshot {
    return {
        id: snapshot.id,
        createdAt: new Date(snapshot.createdAt),
        restoreSize: snapshot.restoreSize,
        type: snapshotTypeOf(snapshot.type),
    }
}

export interface ClusterAutoPauseInformation {
    autoPaused: boolean;
    pauseDueAt?: Date;
    inactiveSince?: Date;
}

export function clusterAutoPauseInformationOf(proto: ClusterCtlProtoCheckClusterAutoPauseRes): ClusterAutoPauseInformation {
    return {
        pauseDueAt: proto.hasPauseDueAt ? new Date(proto.pauseDueAt) : undefined,
        inactiveSince: proto.hasInactiveSince ? new Date(proto.inactiveSince) : undefined,
        autoPaused: proto.autoPaused,
    }
}
